import { jsxs, jsx } from 'react/jsx-runtime';
import emotionStyled from '@emotion/styled';
import { ThemeProvider as ThemeProvider$1, useTheme as useTheme$1 } from 'emotion-theming';
import omit from 'lodash/omit';
import merge from 'lodash/merge';
import isEmpty from 'lodash/isEmpty';
import { useMemo, createContext, useContext } from 'react';
import { accountGroupNavTheme } from './components/nav/accountGroup.js';
import { cardHolderOnlyNavTheme } from './components/nav/cardHolderOnly.js';
import { navThemeFromPartnerTheme } from './components/nav/partner.js';
import { baseTheme } from './base.js';
import { getLocalizedToken } from './localized.js';
import { getBrandedColor } from './partner.js';
import { getComponentThemes } from './components/index.js';
import { mapOverrideTokens } from './customization/index.js';
import { verifyPalette, verifyTypographyTheme, verifyComponentTheme, sanitizeTheme } from './customization/utils.js';
import GlobalThemeStyle from './customization/GlobalThemeStyle.js';
export { GlobalStyles, GlobalStyles20200515 } from './globalStyles/GlobalStyles.js';
export { resetStyles, resetStyles20200515 } from './globalStyles/resetStyles.js';
function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _object_without_properties_loose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
var getPartnerThemeOverride = partnerTheme => {
  var partnerNavTheme = partnerTheme.navTheme && navThemeFromPartnerTheme(partnerTheme.navTheme);
  return _extends({}, partnerNavTheme ? {
    nav: partnerNavTheme
  } : null, {
    partnerLogoUrl: partnerTheme.logoUrl
  });
};
// just exported for testing/docs; try to use ThemeProvider instead
var getTheme = param => {
  var {
    langKey,
    partnerTheme,
    themeOverride,
    contextType = "default"
  } = param;
  // if themeOverride is provided, override the base tokens
  var _ref = themeOverride || {},
    {
      components: componentsOverride,
      typography: typographyOverrideWithFont
    } = _ref,
    tokensOverride = _object_without_properties_loose(_ref, ["components", "typography"]);
  var baseThemedTokens = merge({}, _extends({}, baseTheme, {
    colors: Object.assign({}, baseTheme.colors)
  }), tokensOverride.palette && verifyPalette(tokensOverride.palette) ? mapOverrideTokens(tokensOverride) : {});
  var _ref1 = omit(typographyOverrideWithFont, "fontFace", "enableCustomFontGlobally") || {},
    {
      fontFamily: fontFamilyOverride
    } = _ref1,
    typographyOverride = _object_without_properties_loose(_ref1, ["fontFamily"]); // trim fontFace from theme override tokens
  // themed tokens with localized typography tokens
  var themedTokens = merge(baseThemedTokens, getLocalizedToken({
    langKey,
    theme: baseThemedTokens,
    fontFamily: fontFamilyOverride
  }), _extends({}, typographyOverride && verifyTypographyTheme(typographyOverride) && {
    typography: typographyOverride
  }));
  // components theming
  var generalComponentTokens = getComponentThemes(themedTokens);
  // component level specific theming
  var components = merge(generalComponentTokens, componentsOverride && verifyComponentTheme(componentsOverride) ? componentsOverride : {});
  // partner theme (legacy)
  var partnerThemeOverride = partnerTheme ? getPartnerThemeOverride(partnerTheme) : null;
  if (contextType === "account-group") {
    return _extends({}, themedTokens, components, {
      nav: accountGroupNavTheme
    });
  }
  if (contextType === "cardholder-only") {
    return _extends({}, themedTokens, components, {
      nav: cardHolderOnlyNavTheme
    }, partnerThemeOverride);
  }
  return _extends({}, themedTokens, components, partnerThemeOverride);
};
// use this to provide the right theme
var ThemeProvider = _param => {
  var {
      children
    } = _param,
    themeOptions = _object_without_properties_loose(_param, ["children"]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  var sanitizedThemeOptions = _extends({}, themeOptions, themeOptions.themeOverride && {
    themeOverride: sanitizeTheme(themeOptions.themeOverride)
  });
  var theme = useMemo(() => getTheme(sanitizedThemeOptions), [sanitizedThemeOptions]);
  return /*#__PURE__*/jsxs(ThemeProvider$1, {
    theme: theme,
    children: [/*#__PURE__*/jsx(GlobalThemeStyle, _extends({}, sanitizedThemeOptions == null ? void 0 : sanitizedThemeOptions.themeOverride)), children]
  });
};
var PartnerThemeConfigContext = /*#__PURE__*/createContext(undefined);
var PartnerThemeConfigProvider = _param => {
  var {
      value
    } = _param,
    etc = _object_without_properties_loose(_param, ["value"]);
  return /*#__PURE__*/jsx(PartnerThemeConfigContext.Provider, _extends({
    value: value
  }, etc));
};
var PartnerBrandingThemeMapper = props => {
  var partnerTheme = useContext(PartnerThemeConfigContext);
  return /*#__PURE__*/jsx(ThemeProvider$1, _extends({
    theme: parentTheme => {
      var brandColor = getBrandedColor({
        partnerTheme,
        defaultColors: parentTheme.colors
      });
      return _extends({}, parentTheme, brandColor, getComponentThemes(_extends({}, parentTheme, brandColor)));
    }
  }, props));
};
// as per emotion guidelines.  Use these whenever you want to use this theme (type safely)
var styled = emotionStyled;
var useTheme = useTheme$1;
// helper to default theme to themes.en
// - temporary until all common component users have added ThemeProvider
var withFallback = theme => !theme || isEmpty(theme) ? getTheme({
  langKey: "en"
}) : theme;
export { PartnerBrandingThemeMapper, PartnerThemeConfigContext, PartnerThemeConfigProvider, ThemeProvider, getTheme, navThemeFromPartnerTheme, styled, useTheme, withFallback };
//# sourceMappingURL=data:application/json;charset=utf-8;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiaW5kZXguanMiLCJzb3VyY2VzIjpbXSwic291cmNlc0NvbnRlbnQiOltdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiIn0=
